import DeleteIcon from '@mui/icons-material/Delete';
import {
  IconButton,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
} from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import type { HpgFormValues } from '../schemas';

function focusSameFieldInRow(
  target: HTMLElement,
  selector: string,
  direction: 'prev' | 'next',
): boolean {
  const row = target.closest('tr');
  const sibling =
    direction === 'prev'
      ? row?.previousElementSibling
      : row?.nextElementSibling;
  const input = sibling?.querySelector<HTMLInputElement>(selector);
  if (input) {
    input.focus();
    return true;
  }
  return false;
}

function handleValorKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
  if (['e', 'E', '+', '-', '.', ','].includes(e.key)) {
    e.preventDefault();
    return;
  }
  if (e.key === 'Enter') {
    const all = Array.from(
      document.querySelectorAll<HTMLInputElement>('[data-valor-field="true"]'),
    );
    const idx = all.indexOf(e.target as HTMLInputElement);
    if (idx >= 0 && idx < all.length - 1) {
      e.preventDefault();
      all[idx + 1]?.focus();
    }
  }
  if (!e.shiftKey) return;
  if (e.key === 'ArrowRight') {
    const row = (e.target as HTMLElement).closest('tr');
    const input = row?.querySelector<HTMLInputElement>('[data-identificacion-field="true"]');
    if (input) { e.preventDefault(); input.focus(); }
  }
  if (e.key === 'ArrowLeft') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-observacion-field="true"]', 'prev');
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-valor-field="true"]', 'prev');
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-valor-field="true"]', 'next');
  }
}

function handleIdentificacionKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
  if (!e.shiftKey) return;
  if (e.key === 'ArrowRight') {
    const row = (e.target as HTMLElement).closest('tr');
    const input = row?.querySelector<HTMLInputElement>('[data-observacion-field="true"]');
    if (input) { e.preventDefault(); input.focus(); }
  }
  if (e.key === 'ArrowLeft') {
    const row = (e.target as HTMLElement).closest('tr');
    const input = row?.querySelector<HTMLInputElement>('[data-valor-field="true"]');
    if (input) { e.preventDefault(); input.focus(); }
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-identificacion-field="true"]', 'prev');
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-identificacion-field="true"]', 'next');
  }
}

function handleObservacionKeyDown(e: React.KeyboardEvent<HTMLInputElement>): void {
  if (!e.shiftKey) return;
  if (e.key === 'ArrowRight') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-valor-field="true"]', 'next');
  }
  if (e.key === 'ArrowLeft') {
    const input = e.target as HTMLInputElement;
    if (input.selectionStart === 0) {
      const row = input.closest('tr');
      const identificacionInput = row?.querySelector<HTMLInputElement>('[data-identificacion-field="true"]');
      if (identificacionInput) { e.preventDefault(); identificacionInput.focus(); }
    }
  }
  if (e.key === 'ArrowUp') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-observacion-field="true"]', 'prev');
  }
  if (e.key === 'ArrowDown') {
    e.preventDefault();
    focusSameFieldInRow(e.target as HTMLElement, '[data-observacion-field="true"]', 'next');
  }
}

interface HpgFormMuestraRowProps {
  index: number;
  onRemove: () => void;
  canRemove: boolean;
}

export function HpgFormMuestraRow({
  index,
  onRemove,
  canRemove,
}: HpgFormMuestraRowProps): JSX.Element {
  const { control, formState } = useFormContext<HpgFormValues>();
  const errors = formState.errors;
  const muestraErr = errors.muestras?.[index];

  return (
    <TableRow>
      <TableCell sx={{ width: 50, textAlign: 'center' }}>{index + 1}</TableCell>

      <TableCell sx={{ width: 120 }}>
        <Controller
          control={control}
          name={`muestras.${index}.valor`}
          render={({ field, fieldState }) => (
            <TextField
              size="small"
              type="number"
              inputProps={{ min: 0, 'data-valor-field': 'true' }}
              onKeyDown={handleValorKeyDown}
              error={!!fieldState.error || !!muestraErr?.valor}
              helperText={fieldState.error?.message ?? muestraErr?.valor?.message}
              value={field.value !== null ? String(field.value) : ''}
              onChange={(e) => {
                const v = e.target.value;
                if (v === '') {
                  field.onChange(null);
                  return;
                }
                const parsed = Number(v);
                if (!isNaN(parsed)) field.onChange(parsed);
              }}
              onBlur={field.onBlur}
              sx={{ width: 120 }}
            />
          )}
        />
      </TableCell>

      <TableCell sx={{ width: 160 }}>
        <Controller
          control={control}
          name={`muestras.${index}.identificacion`}
          render={({ field }) => (
            <TextField
              size="small"
              fullWidth
              placeholder="ej: A-12"
              inputProps={{ 'data-identificacion-field': 'true' }}
              onKeyDown={handleIdentificacionKeyDown}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value !== '' ? e.target.value : null)}
              onBlur={field.onBlur}
            />
          )}
        />
      </TableCell>

      <TableCell>
        <Controller
          control={control}
          name={`muestras.${index}.observacion`}
          render={({ field }) => (
            <TextField
              size="small"
              fullWidth
              inputProps={{ 'data-observacion-field': 'true' }}
              onKeyDown={handleObservacionKeyDown}
              value={field.value ?? ''}
              onChange={(e) => field.onChange(e.target.value !== '' ? e.target.value : null)}
              onBlur={field.onBlur}
            />
          )}
        />
      </TableCell>

      <TableCell sx={{ width: 50, textAlign: 'center' }}>
        <Tooltip title={!canRemove ? 'Tiene que haber al menos una muestra.' : ''}>
          <span>
            <IconButton
              size="small"
              color="error"
              disabled={!canRemove}
              onClick={onRemove}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}
